import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Avatar,
  Tooltip,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Link,
  Divider,
  Button
} from '@mui/material';
import {
  Menu as MenuIcon,
  ShoppingCart as ShoppingCartIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  Info as InfoIcon,
  Category as CategoryIcon,
  Receipt as ReceiptIcon,
  LocalMall as LocalMallIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

function Navbar() {
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const { currentUser, logout } = useAuth();
  const { cartItemCount } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'categories'));
        const categoriesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCategories(categoriesData);
      } catch (error) {
        console.error('Kategoriler yüklenirken hata:', error);
      }
    };

    fetchCategories();
  }, []);

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Çıkış yapılırken hata:', error);
    }
  };

  return (
    <AppBar position="fixed" sx={{ bgcolor: 'white', color: 'text.primary' }}>
      <Container maxWidth="lg">
        <Toolbar disableGutters>
          <IconButton
            color="inherit"
            aria-label="menu"
            onClick={handleMobileMenuToggle}
            sx={{ 
              display: { md: 'none' },
              color: 'text.primary',
              mr: 1
            }}
          >
            {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>

          <Typography
            variant="h6"
            noWrap
            component={RouterLink}
            to="/"
            sx={{
              mr: 2,
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
            }}
          >
            <Box
              component="img"
              src="/images/logo.jpg"
              alt="Kayla Logo"
              sx={{
                height: { xs: '35px', md: '50px' },
                width: 'auto',
                objectFit: 'contain'
              }}
            />
          </Typography>

          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            <Link
              component={RouterLink}
              to="/"
              color="inherit"
              sx={{ mx: 2, textDecoration: 'none' }}
            >
              Ana Sayfa
            </Link>
            <Link
              component={RouterLink}
              to="/categories"
              color="inherit"
              sx={{ mx: 2, textDecoration: 'none' }}
            >
              Kategoriler
            </Link>
            <Link
              component={RouterLink}
              to="/about"
              color="inherit"
              sx={{ mx: 2, textDecoration: 'none' }}
            >
              Hakkımızda
            </Link>
          </Box>

          <Box sx={{ 
            display: { xs: 'none', md: 'flex' },
            gap: 2,
            ml: 4
          }}>
            {categories.map((category) => (
              <Button
                key={category.id}
                component={RouterLink}
                to={`/category/${category.slug}`}
                sx={{
                  color: 'text.primary',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                {category.name}
              </Button>
            ))}
          </Box>

          <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center' }}>
            <IconButton
              component={RouterLink}
              to="/cart"
              color="inherit"
              sx={{ mr: 2 }}
            >
              <Badge badgeContent={cartItemCount} color="error">
                <ShoppingCartIcon />
              </Badge>
            </IconButton>

            {currentUser ? (
              <>
                <Tooltip title="Hesap işlemleri">
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: 'primary.main',
                        color: 'white',
                        width: 35,
                        height: 35,
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        '&:hover': {
                          bgcolor: 'primary.dark'
                        }
                      }}
                    >
                      {currentUser.email?.charAt(0).toUpperCase()}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ mt: '45px' }}
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  <MenuItem component={RouterLink} to="/profile">
                    <ListItemIcon>
                      <PersonIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Profilim</ListItemText>
                  </MenuItem>
                  <MenuItem component={RouterLink} to="/my-orders">
                    <ListItemIcon>
                      <ReceiptIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Siparişlerim</ListItemText>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <ListItemText>Çıkış Yap</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button
                component={RouterLink}
                to="/auth/login"
                color="inherit"
              >
                Giriş Yap
              </Button>
            )}
          </Box>
        </Toolbar>
      </Container>

      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
        PaperProps={{
          sx: {
            width: 280,
            bgcolor: 'background.default',
            borderRight: 'none'
          }
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Box
            component="img"
            src="/images/logo.jpg"
            alt="Kayla Logo"
            sx={{
              height: '40px',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
        </Box>

        <List sx={{ pt: 1 }}>
          <ListItem 
            component={RouterLink} 
            to="/"
            onClick={handleMobileMenuToggle}
            sx={{
              py: 1.5,
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
          >
            <ListItemIcon>
              <HomeIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Ana Sayfa"
              primaryTypographyProps={{
                fontWeight: 500
              }}
            />
          </ListItem>

          <ListItem 
            component={RouterLink} 
            to="/categories"
            onClick={handleMobileMenuToggle}
            sx={{
              py: 1.5,
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
          >
            <ListItemIcon>
              <CategoryIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Kategoriler"
              primaryTypographyProps={{
                fontWeight: 500
              }}
            />
          </ListItem>

          <ListItem 
            component={RouterLink} 
            to="/products"
            onClick={handleMobileMenuToggle}
            sx={{
              py: 1.5,
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
          >
            <ListItemIcon>
              <LocalMallIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Ürünler"
              primaryTypographyProps={{
                fontWeight: 500
              }}
            />
          </ListItem>

          <ListItem 
            component={RouterLink} 
            to="/about"
            onClick={handleMobileMenuToggle}
            sx={{
              py: 1.5,
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
          >
            <ListItemIcon>
              <InfoIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Hakkımızda"
              primaryTypographyProps={{
                fontWeight: 500
              }}
            />
          </ListItem>

          <Divider sx={{ my: 1 }} />

          {categories.map((category) => (
            <ListItem 
              key={category.id}
              component={RouterLink}
              to={`/category/${category.slug}`}
              onClick={handleMobileMenuToggle}
              sx={{
                py: 1.5,
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              <ListItemIcon>
                <CategoryIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary={category.name}
                primaryTypographyProps={{
                  fontWeight: 500
                }}
              />
            </ListItem>
          ))}

          {currentUser && (
            <>
              <Divider sx={{ my: 1 }} />
              <ListItem 
                component={RouterLink} 
                to="/my-orders"
                onClick={handleMobileMenuToggle}
                sx={{
                  py: 1.5,
                  '&:hover': {
                    bgcolor: 'action.hover'
                  }
                }}
              >
                <ListItemIcon>
                  <ReceiptIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Siparişlerim"
                  primaryTypographyProps={{
                    fontWeight: 500
                  }}
                />
              </ListItem>
            </>
          )}
        </List>
      </Drawer>
    </AppBar>
  );
}

export default Navbar; 